import type { BoardData } from '../model/schema.js';
import { commentThreads } from './threads.js';
import { identityKey } from './visits.js';
import type {
  AnchorLike,
  CatchupFilter,
  CatchupSummary,
  Change,
  CommentLike,
  OpenItemLike,
  PersonRef,
  RelevanceContext,
} from './types.js';

/** No filtering: all topics, all types, not only what concerns me. */
export const ALL_FILTER: CatchupFilter = { topics: null, concernsMe: false, types: null };

/** Mention lists hold identity keys (`member:<id>` / `guest:<id>`); bare ids are accepted too. */
function mentionsMe(mentions: readonly string[] | undefined, me: PersonRef): boolean {
  return mentions !== undefined && (mentions.includes(identityKey(me)) || mentions.includes(me.id));
}

function isMe(p: PersonRef | null, me: PersonRef): boolean {
  return p !== null && p.kind === me.kind && p.id === me.id;
}

/** I wrote a comment of the thread (so replies are replies to me) or I am mentioned in it. */
function threadConcerns(thread: readonly CommentLike[], me: PersonRef): boolean {
  return thread.some((c) => isMe(c.author, me) || mentionsMe(c.mentions, me));
}

function openItemConcerns(item: OpenItemLike, me: PersonRef): boolean {
  return (
    isMe(item.assignee, me) ||
    isMe(item.originator, me) ||
    (item.replies ?? []).some((r) => isMe(r.author, me) || mentionsMe(r.mentions, me))
  );
}

function anchoredTo(anchor: AnchorLike | null, id: string): boolean {
  return anchor !== null && anchor.type === 'item' && anchor.itemId === id;
}

/**
 * "Concerns me" (decision C3): I am mentioned, assigned, the originator, or someone replied to
 * me; a plot change concerns me when its object carries such a thread / open item or is a
 * free-form element I authored. A relevance filter only (decision U6), never a permission check.
 */
export function changeConcernsMe(change: Change, ctx: RelevanceContext, data?: BoardData): boolean {
  const { me } = ctx;
  if (change.type === 'comments') {
    const thread = commentThreads(ctx.comments).get(change.id);
    return thread !== undefined && threadConcerns(thread, me);
  }
  if (change.type !== 'plot') {
    const item = ctx.openItems.find((i) => i.id === change.id);
    return item !== undefined && openItemConcerns(item, me);
  }
  if (change.collection === 'freeform' && data?.freeform[change.id]?.authorId === me.id)
    return true;
  if (change.collection !== 'items' && change.collection !== 'freeform') return false;
  for (const thread of commentThreads(ctx.comments).values())
    if (anchoredTo(thread[0]!.anchor, change.id) && threadConcerns(thread, me)) return true;
  return ctx.openItems.some((i) => anchoredTo(i.anchor, change.id) && openItemConcerns(i, me));
}

export function matchesFilter(
  change: Change,
  filter: CatchupFilter,
  ctx: RelevanceContext,
  data?: BoardData,
): boolean {
  if (filter.types !== null && !filter.types.includes(change.type)) return false;
  if (filter.topics !== null && !filter.topics.includes(change.topic)) return false;
  return !filter.concernsMe || changeConcernsMe(change, ctx, data);
}

export function filterChanges<C extends Change>(
  changes: readonly C[],
  filter: CatchupFilter,
  ctx: RelevanceContext,
  data?: BoardData,
): C[] {
  return changes.filter((c) => matchesFilter(c, filter, ctx, data));
}

/** Banner numbers (decision C5): all changes, those concerning me, per type. */
export function summarize(
  changes: readonly Change[],
  ctx: RelevanceContext,
  data?: BoardData,
): CatchupSummary {
  const byType = { plot: 0, comments: 0, openitems: 0 };
  let concerningMe = 0;
  for (const c of changes) {
    byType[c.type] += 1;
    if (changeConcernsMe(c, ctx, data)) concerningMe += 1;
  }
  return { total: changes.length, concerningMe, byType };
}
